import React, { useEffect } from 'react';
import { ArrowLeft, Check, Calendar, Droplet, Milestone, Car } from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { usePendingMrcStore } from '../store/usePendingMrcStore';
import { OLYMPIC_AGENCIES } from '../utils/agencies';
import Card from '../components/ui/Card';

/** Convertit une valeur 0-8 (MRC) en pourcentage lisible */
function renderFuelPct(value: number | null | undefined): string {
  if (value == null) return '—';
  const safeVal = Math.max(0, Math.min(8, value));
  return `${Math.round(safeVal / 8 * 100)}%`;
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

export default function PendingMrcScreen() {
  const setStep             = useAppStore(s => s.setStep);
  const selectedAgency      = useAppStore(s => s.selectedAgency);
  const pendingReturns      = usePendingMrcStore(s => s.pendingReturns);
  const removePendingReturn = usePendingMrcStore(s => s.removePendingReturn);

  // ✅ Filtrage par agence active
  // selectedAgency = id agence (ex: "121" = Plombières → code "09")
  // pendingReturns.agencyCode = code string (ex: "09")
  // Règle métier : l'agent voit uniquement les retours de son agence de sync.
  // Sans agence sélectionnée (cas admin) : afficher tout.
  const activeAgency = selectedAgency
    ? OLYMPIC_AGENCIES.find(a => a.id === String(selectedAgency)) ?? null
    : null

  const filteredReturns = activeAgency
    ? pendingReturns.filter(r => r.agencyCode === activeAgency.code)
    : pendingReturns

  // Auto-retour si la liste de l'agence active devient vide
  useEffect(() => {
    if (filteredReturns.length === 0) {
      setStep('contracts');
    }
  }, [filteredReturns.length, setStep]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Header */}
      <div className="bg-red-500 text-white px-4 py-3 shadow-md flex items-center gap-3 sticky top-0 z-10">
        <button
          onClick={() => setStep('contracts')}
          className="p-2 -ml-2 rounded-full hover:bg-white/20 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-lg font-bold">Retours à saisir (MyRentCar)</h1>
          <p className="text-xs text-red-100">
            {filteredReturns.length} véhicule{filteredReturns.length > 1 ? 's' : ''} en attente
            {activeAgency && ` — Agence ${activeAgency.code} ${activeAgency.name}`}
          </p>
        </div>
      </div>

      <div className="flex-1 p-4 flex flex-col gap-4">
        {filteredReturns.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Check size={48} className="mb-4 text-green-500" />
            <p className="font-semibold text-gray-600">Tout est à jour</p>
            <p className="text-sm text-gray-500">Aucun retour en attente de saisie</p>
          </div>
        ) : (
          filteredReturns.map(item => {
            const diffKm = (item.kmRetour != null && item.kmDepart != null)
              ? item.kmRetour - item.kmDepart
              : null;

            return (
              <Card key={item.id} className="p-4 border-l-4 border-red-500 shadow-sm flex flex-col gap-3">

                {/* En-tête */}
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-black text-gray-900 text-lg tracking-wide">{item.plate}</span>
                      {item.agencyCode && (
                        <span className="bg-gray-100 text-gray-600 text-xs font-bold px-1.5 py-0.5 rounded">
                          {item.agencyCode}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <Car size={12} /> {item.vehicleName}
                    </p>
                    <p className="text-sm font-semibold text-gray-700 mt-1">{item.clientName}</p>
                  </div>

                  <div className="text-right">
                    <p className="text-xs font-semibold text-gray-500 flex items-center gap-1 justify-end">
                      <Calendar size={12} /> {fmtDate(item.completedAt)}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">Par {item.agentName}</p>
                  </div>
                </div>

                {/* Comparatif technique */}
                <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-2 border border-gray-100">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm">
                    <span className="text-gray-500 font-semibold flex items-center gap-1.5 min-w-[90px]">
                      <Milestone size={14} /> Km
                    </span>
                    <div className="flex items-center gap-2 text-gray-800 flex-1">
                      <span className="tabular-nums text-gray-500">{item.kmDepart != null ? item.kmDepart : '—'}</span>
                      <span className="text-gray-300">→</span>
                      <span className="tabular-nums font-bold">{item.kmRetour != null ? item.kmRetour : '—'}</span>
                      {diffKm != null && (
                        <span className="text-blue-600 text-xs font-bold bg-blue-50 px-1.5 rounded ml-auto">
                          +{diffKm} km
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="h-px bg-gray-200 w-full" />

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between text-sm">
                    <span className="text-gray-500 font-semibold flex items-center gap-1.5 min-w-[90px]">
                      <Droplet size={14} /> Carburant
                    </span>
                    <div className="flex items-center gap-2 text-gray-800 flex-1">
                      <span className="text-sm font-semibold text-gray-500">{renderFuelPct(item.fuelDepart)}</span>
                      <span className="text-gray-300">→</span>
                      <span className="text-sm font-bold">{renderFuelPct(item.fuelRetour)}</span>
                    </div>
                  </div>
                </div>

                {/* Action */}
                <div className="pt-2">
                  <button
                    onClick={() => removePendingReturn(item.id)}
                    className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 py-2.5 rounded-lg font-bold text-sm transition-colors active:scale-95"
                  >
                    <Check size={18} />
                    Marquer comme saisi dans MyRentCar
                  </button>
                </div>

              </Card>
            )
          })
        )}
      </div>
    </div>
  );
}
